/**
 * Custom hook encapsulating all form state and handlers for the POC-reassignment modal.
 */

import { useEffect, useState } from "react";
import type { PocReassignmentRuleFormData } from "../../../data-model/pocReassignmentRule";
import {
  cloneActionValue,
  toValueArray,
  toValueString,
} from "../shared/ruleFormConstants";

const defaultFormData: PocReassignmentRuleFormData = {
  label: "",
  subtext: "",
  active: true,
  lookback: "0 days",
  actions: [{ type: "set", field: "poc", value: "" }],
  triggers: [{ type: "api" }],
};

const cloneFormData = (
  data: PocReassignmentRuleFormData,
): PocReassignmentRuleFormData => ({
  ...data,
  actions: data.actions.map((a) => ({
    ...a,
    value: cloneActionValue(a.value),
  })),
  triggers: data.triggers.map((t) => ({ ...t })),
});

interface UsePocReassignmentFormParams {
  initialData?: PocReassignmentRuleFormData;
  onSave: (data: PocReassignmentRuleFormData) => void | Promise<void>;
  onClose: () => void;
  onCancel: () => void;
}

/**
 * Manages form state, field handlers, and save/cancel logic for the POC-reassignment modal.
 *
 * @param params - Callbacks and optional initial data for edit mode.
 * @returns Form state, individual field handlers, and modal action handlers.
 */
export const usePocReassignmentForm = ({
  initialData,
  onSave,
  onClose,
  onCancel,
}: UsePocReassignmentFormParams) => {
  const [form, setForm] = useState<PocReassignmentRuleFormData>(
    initialData ? cloneFormData(initialData) : cloneFormData(defaultFormData),
  );
  const [formKey, setFormKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const isEditMode = initialData != null;

  useEffect(() => {
    if (initialData != null) {
      setForm(cloneFormData(initialData));
      setFormKey((k) => k + 1);
    }
  }, [initialData]);

  const resetForm = () => {
    setForm(cloneFormData(defaultFormData));
    setFormKey((k) => k + 1);
  };

  const updateField = <K extends keyof PocReassignmentRuleFormData>(
    key: K,
    value: PocReassignmentRuleFormData[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleActionFieldChange = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.map((a, i) =>
        i === index ? { ...a, field: value } : a,
      ),
    }));
  };

  const handleActionTypeChange = (index: number, newType: string) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.map((a, i) => {
        if (i !== index) return a;
        const wasSetRandom = a.type === "set_random";
        const isSetRandom = newType === "set_random";
        let newValue: string | string[] = a.value;
        if (!wasSetRandom && isSetRandom) {
          newValue = toValueArray(a.value);
        } else if (wasSetRandom && !isSetRandom) {
          newValue = toValueString(a.value);
        }
        return { ...a, type: newType, value: newValue };
      }),
    }));
  };

  /** Sets a single-string value for non-set_random action types. */
  const handleActionSingleValueChange = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.map((a, i) => (i === index ? { ...a, value } : a)),
    }));
  };

  const handleActionMultiValueChange = (index: number, values: string[]) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.map((a, i) =>
        i === index ? { ...a, value: values } : a,
      ),
    }));
  };

  const handleAddAction = () => {
    setForm((prev) => ({
      ...prev,
      actions: [...prev.actions, { type: "set", field: "", value: "" }],
    }));
  };

  const handleRemoveAction = (index: number) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index),
    }));
  };

  const handleTriggerTypeChange = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      triggers: prev.triggers.map((t, i) =>
        i === index ? { ...t, type: value } : t,
      ),
    }));
  };

  const handleTriggerValueChange = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      triggers: prev.triggers.map((t, i) =>
        i === index ? { ...t, value } : t,
      ),
    }));
  };

  const handleAddTrigger = () => {
    setForm((prev) => ({
      ...prev,
      triggers: [...prev.triggers, { type: "api" }],
    }));
  };

  const handleRemoveTrigger = (index: number) => {
    setForm((prev) => ({
      ...prev,
      triggers: prev.triggers.filter((_, i) => i !== index),
    }));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const handleSave = async () => {
    /** Lookback is not editable in the modal; preserve the value loaded from Firestore on edit. */
    const lookback =
      isEditMode && initialData != null
        ? initialData.lookback
        : form.lookback.trim() || "0 days";

    const data: PocReassignmentRuleFormData = {
      label: form.label.trim() || "New Rule",
      subtext: form.subtext.trim(),
      active: form.active,
      lookback,
      actions: form.actions
        .filter((a) => {
          const valArr = toValueArray(a.value);
          const hasValue = valArr.some((v) => v.trim() !== "");
          return a.field.trim() !== "" || hasValue || a.type.trim() !== "";
        })
        .map((a) => {
          const trimmedType = a.type.trim() || "set";
          const cleanedArr = toValueArray(a.value)
            .map((v) => v.trim())
            .filter((v) => v !== "");
          if (trimmedType === "set_random") {
            return {
              type: trimmedType,
              field: a.field.trim(),
              value: cleanedArr,
            };
          }
          return {
            type: trimmedType,
            field: a.field.trim(),
            value: cleanedArr.join(", "),
          };
        }),
      triggers: form.triggers.map((t) => ({ ...t })),
    };
    setIsSaving(true);
    try {
      await onSave(data);
      resetForm();
    } finally {
      setIsSaving(false);
    }
  };

  return {
    form,
    formKey,
    isSaving,
    isEditMode,
    setForm,
    updateField,
    handleActionFieldChange,
    handleActionTypeChange,
    handleActionSingleValueChange,
    handleActionMultiValueChange,
    handleAddAction,
    handleRemoveAction,
    handleTriggerTypeChange,
    handleTriggerValueChange,
    handleAddTrigger,
    handleRemoveTrigger,
    handleClose,
    handleCancel,
    handleSave,
  };
};
